/**
 * Report Builder - PDF Export
 *
 * Exports the report canvas content to PDF as text
 */

(function () {
  "use strict";

  /**
   * Get jsPDF constructor, handling both UMD and global formats
   */
  function getJsPDF() {
    // Try UMD format first (newer versions)
    if (window.jspdf && window.jspdf.jsPDF) {
      return window.jspdf.jsPDF;
    }
    // Try global format (older versions)
    if (window.jsPDF) {
      return window.jsPDF;
    }
    return null;
  }

  /**
   * Wait for libraries to be available
   */
  function waitForLibrariesReady(callback, maxAttempts = 50, attempt = 0) {
    const JsPDF = getJsPDF();
    const hasHtml2Canvas = typeof html2canvas !== "undefined";

    if (JsPDF && hasHtml2Canvas) {
      callback(JsPDF);
      return;
    }

    if (attempt >= maxAttempts) {
      console.error("Libraries failed to load after waiting");
      console.log("jsPDF available:", !!JsPDF);
      console.log("html2canvas available:", hasHtml2Canvas);
      alert("Error: PDF export libraries not loaded. Please refresh the page.");
      return;
    }

    setTimeout(function () {
      waitForLibrariesReady(callback, maxAttempts, attempt + 1);
    }, 100);
  }

  /**
   * Width (CSS px) that charts, calculators and placed images are laid out at
   * for the PDF, in the range a desktop screen gives the item content (about
   * 580-930px depending on the window). Fixing it means the PDF comes out the
   * same whether the report is generated from a phone or a desktop: before,
   * a phone captured a narrow, tall layout that was then scaled up to the
   * page width and looked stretched.
   */
  const CAPTURE_LAYOUT_WIDTH = 640;
  // Viewport width the capture's media queries are evaluated at
  const CAPTURE_WINDOW_WIDTH = 1280;

  /**
   * Re-run Chart.js layout for every chart inside the element so its canvas
   * matches the element's current width (resize is normally driven by a
   * ResizeObserver, which is asynchronous).
   */
  function resizeChartsIn(element) {
    if (typeof Chart === "undefined" || typeof Chart.getChart !== "function") {
      return;
    }
    element.querySelectorAll("canvas").forEach(function (canvas) {
      const chart = Chart.getChart(canvas);
      if (chart) {
        chart.resize();
      }
    });
  }

  /**
   * Lay the element out at the fixed capture width. Resolves with a restore
   * function once the browser has re-laid out the element and its charts.
   */
  function applyCaptureLayout(element) {
    const saved = {
      width: element.style.width,
      maxWidth: element.style.maxWidth,
      minWidth: element.style.minWidth,
    };
    element.style.width = CAPTURE_LAYOUT_WIDTH + "px";
    element.style.maxWidth = "none";
    element.style.minWidth = "0";
    resizeChartsIn(element);

    function restore() {
      element.style.width = saved.width;
      element.style.maxWidth = saved.maxWidth;
      element.style.minWidth = saved.minWidth;
      resizeChartsIn(element);
    }

    return new Promise(function (resolve) {
      // Two frames: one for layout, one for Chart.js to paint at the new size
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          resolve(restore);
        });
      });
    });
  }

  /**
   * html2canvas renders a clone of the page inside an off-screen iframe sized
   * to `windowWidth`, and media queries in the clone follow that iframe. The
   * theme's responsive `iframe { max-width: 100% }` rule squashes that iframe
   * to the real viewport, so on a phone the clone still used the mobile
   * styles. Exempt the html2canvas iframe from it.
   */
  function ensureCaptureStyles() {
    if (document.getElementById("mchri-pdf-capture-styles")) return;
    const style = document.createElement("style");
    style.id = "mchri-pdf-capture-styles";
    style.textContent =
      "iframe.html2canvas-container { max-width: none !important; min-width: 0 !important; }";
    document.head.appendChild(style);
  }

  /**
   * Capture element as image using html2canvas, laid out at the fixed
   * capture width so the result does not depend on the screen size.
   */
  function captureElementAsImage(element) {
    if (typeof html2canvas === "undefined") {
      return Promise.reject(new Error("html2canvas not available"));
    }

    ensureCaptureStyles();

    return applyCaptureLayout(element).then(function (restoreLayout) {
      return captureLaidOutElement(element).then(
        function (result) {
          restoreLayout();
          return result;
        },
        function (error) {
          restoreLayout();
          throw error;
        }
      );
    });
  }

  // Hidden while capturing: controls, recommendations (drawn as text so
  // links stay clickable) and empty calculator descriptions
  const CAPTURE_EXCLUDED_SELECTOR =
    "button, input, select, .chart-recommendation, .rb-calc-description.is-empty";

  /**
   * Hide the elements left out of a capture. Returns a function to show them.
   */
  function hideCaptureExcluded(element) {
    const hidden = element.querySelectorAll(CAPTURE_EXCLUDED_SELECTOR);
    const originalDisplay = [];
    hidden.forEach(function (el, index) {
      originalDisplay[index] = el.style.display;
      el.style.display = "none";
    });
    return function restore() {
      hidden.forEach(function (el, index) {
        el.style.display = originalDisplay[index];
      });
    };
  }

  function captureLaidOutElement(element) {
    return new Promise(function (resolve, reject) {

      const restoreExcluded = hideCaptureExcluded(element);

      // Hide ::before pseudo-elements on calculator elements
      const calculators = element.querySelectorAll(
        ".cost-calculator, .carbon-calculator"
      );
      let styleElement = null;
      if (calculators.length > 0) {
        styleElement = document.createElement("style");
        styleElement.textContent =
          ".cost-calculator::before, .carbon-calculator::before, .cost-calculator::after, .carbon-calculator::after { display: none !important; }";
        document.head.appendChild(styleElement);
      }

      // No explicit width/height: html2canvas measures the element in its
      // clone, which is laid out at the desktop window width below, so the
      // capture covers the desktop layout rather than the live (possibly
      // mobile) one.
      const options = {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        allowTaint: true,
        windowWidth: CAPTURE_WINDOW_WIDTH,
        windowHeight: 900,
        // Record where each link sits in the clone (the layout that is
        // actually captured), so the PDF can make that area clickable
        onclone: function (clonedDoc) {
          const clone = clonedDoc.querySelector("[data-pdf-capture]");
          if (clone) links = measureLinks(clone);
        },
      };
      let links = [];
      element.setAttribute("data-pdf-capture", "1");

      html2canvas(element, options)
        .then(function (canvas) {
          element.removeAttribute("data-pdf-capture");
          restoreExcluded();

          // Remove the style element to restore ::before
          if (styleElement) {
            document.head.removeChild(styleElement);
          }

          const imgData = canvas.toDataURL("image/png");
          resolve({
            data: imgData,
            width: canvas.width,
            height: canvas.height,
            // CSS-pixel size of what was captured (the clone's layout)
            displayWidth: canvas.width / options.scale,
            displayHeight: canvas.height / options.scale,
            links: links,
          });
        })
        .catch(function (error) {
          element.removeAttribute("data-pdf-capture");
          restoreExcluded();

          // Remove the style element to restore ::before
          if (styleElement) {
            document.head.removeChild(styleElement);
          }

          reject(error);
        });
    });
  }

  /**
   * Links inside an element as {url, x, y, width, height} in CSS px relative
   * to the element's top-left corner. One entry per line box, so a link that
   * wraps gets a clickable area on each of its lines.
   */
  function measureLinks(element) {
    const origin = element.getBoundingClientRect();
    const found = [];
    element.querySelectorAll("a[href]").forEach(function (anchor) {
      let url;
      try {
        url = new URL(anchor.getAttribute("href"), window.location.href);
      } catch (e) {
        return;
      }
      if (!/^(https?|mailto|tel):$/.test(url.protocol)) return;
      Array.from(anchor.getClientRects()).forEach(function (rect) {
        if (!rect.width || !rect.height) return;
        found.push({
          url: url.href,
          x: rect.left - origin.left,
          y: rect.top - origin.top,
          width: rect.width,
          height: rect.height,
        });
      });
    });
    return found;
  }

  /**
   * Make the links measured in a captured image clickable where the image
   * was drawn (x, y, width in mm; displayWidth in CSS px).
   */
  function addCapturedLinks(pdf, links, x, y, width, displayWidth) {
    if (!links || !links.length || !displayWidth) return;
    const mmPerPx = width / displayWidth;
    links.forEach(function (link) {
      pdf.link(
        x + link.x * mmPerPx,
        y + link.y * mmPerPx,
        link.width * mmPerPx,
        link.height * mmPerPx,
        { url: link.url }
      );
    });
  }

  /**
   * Extract text content from HTML element, stripping HTML tags
   */
  function extractTextContent(element) {
    if (!element) return "";

    // Clone to avoid modifying original
    const clone = element.cloneNode(true);

    // Remove script and style elements
    const scripts = clone.querySelectorAll("script, style");
    scripts.forEach((el) => el.remove());

    // Get text content
    return clone.textContent || clone.innerText || "";
  }

  /**
   * Parse a computed CSS colour ("rgb(r, g, b)" / "rgba(...)") into [r, g, b].
   * Returns null for anything else, including fully transparent colours.
   */
  function parseCssColor(value) {
    const match = /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+))?/.exec(value || "");
    if (!match || (match[4] !== undefined && parseFloat(match[4]) === 0)) {
      return null;
    }
    return [Number(match[1]), Number(match[2]), Number(match[3])];
  }

  // Horizontal padding inside a pill, in mm.
  const PILL_PADDING_X = 1.8;

  /**
   * Draw a pill run: rounded background, then bold text on top.
   */
  function drawPillRun(pdf, run, lineY) {
    const fontMm = pdf.getFontSize() * 0.3528;
    const height = fontMm * 1.45;
    const top = lineY - fontMm * 0.72 - (height - fontMm * 0.72) / 2 + fontMm * 0.04;
    pdf.setFillColor(run.pill.background[0], run.pill.background[1], run.pill.background[2]);
    pdf.roundedRect(run.x, top, run.width, height, height / 2, height / 2, "F");
    pdf.setFont(undefined, "bold");
    pdf.setTextColor(run.pill.color[0], run.pill.color[1], run.pill.color[2]);
    pdf.text(run.text, run.x + PILL_PADDING_X, lineY);
    pdf.setFont(undefined, "normal");
  }

  /**
   * Extract text as segments, preserving hyperlinks
   * Returns array of { text, url } where url is null for plain text
   */
  function extractTextSegmentsWithLinks(element) {
    const segments = [];

    function walk(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent) {
          segments.push({ text: node.textContent, url: null });
        }
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const tag = node.tagName.toLowerCase();
      if (tag === "script" || tag === "style") return;
      // Opt-in structure for text that should not run together as one
      // paragraph: data-pdf-line starts a new line, data-pdf-gap a new
      // paragraph (blank line first).
      if (node.hasAttribute("data-pdf-gap")) {
        segments.push({ text: "", url: null, lineBreak: true, gap: true });
      } else if (node.hasAttribute("data-pdf-line")) {
        segments.push({ text: "", url: null, lineBreak: true });
      }
      if (tag === "br") {
        segments.push({ text: " ", url: null });
        return;
      }
      // data-pdf-pill: a badge drawn as one unbreakable run on its own
      // background colour, as it looks on screen.
      if (node.hasAttribute("data-pdf-pill")) {
        const text = node.textContent.trim();
        if (text) {
          const style = window.getComputedStyle(node);
          segments.push({
            text: style.textTransform === "uppercase" ? text.toUpperCase() : text,
            url: null,
            pill: {
              background: parseCssColor(style.backgroundColor) || [153, 153, 153],
              color: parseCssColor(style.color) || [255, 255, 255],
            },
          });
          segments.push({ text: " ", url: null });
        }
        return;
      }
      if (tag === "a" && node.href) {
        const text = node.textContent.trim();
        if (text) {
          segments.push({ text: text, url: node.href });
        }
        return;
      }

      Array.from(node.childNodes).forEach(walk);

      // Whitespace separation after block-level elements
      if (tag === "p" || tag === "div" || tag === "li") {
        segments.push({ text: " ", url: null });
      }
    }

    walk(element);
    return segments;
  }

  /* ------------------------------------------------------------------ *
   * Page flow
   *
   * Content is either "atomic" (headings, images, charts, calculators —
   * moved whole) or "flowable" (paragraphs, lists, plain text — allowed to
   * split across pages). Flowable content fills the page it starts on and
   * breaks mid-block, so a long paragraph no longer leaves the rest of a
   * page empty just because it can't fit in one piece.
   * ------------------------------------------------------------------ */

  // Minimum lines a split block leaves on the page it starts on (orphans)
  // and carries onto the page it continues on (widows).
  const MIN_ORPHAN_LINES = 2;
  const MIN_WIDOW_LINES = 2;

  /* ------------------------------------------------------------------ *
   * Patient story media row (MCH-211)
   *
   * The excerpt runs full width, then the featured image and the clickable
   * QR code sit side by side on one row at a matching height. Previously the
   * image, excerpt and a 30mm centred QR were stacked, which left most of the
   * page empty for a short story.
   * ------------------------------------------------------------------ */
  const PS_QR_SIZE = 22; // mm — QR edge length when it stands alone
  const PS_MEDIA_GAP = 6; // mm — gap between the image and the QR
  const PS_IMAGE_MAX_HEIGHT = 50; // mm — caps the row so the block stays tight

  /**
   * Work out the geometry of a patient story's media row.
   *
   * Shared by the height estimate and the renderer so the page-break
   * decision matches what actually gets drawn. Dimensions in, mm out.
   * Either side may be absent; returns nulls for whatever is missing.
   */
  function patientStoryMediaLayout(imageW, imageH, qrW, qrH, maxWidth) {
    const hasImage = imageW > 0 && imageH > 0;
    const hasQr = qrW > 0 && qrH > 0;
    const imageRatio = hasImage ? imageW / imageH : 0;
    const qrRatio = hasQr ? qrW / qrH : 0;

    let image = null;
    let qr = null;

    if (hasImage && hasQr) {
      // Both are drawn at one shared height so the QR reads as part of the
      // image row rather than a small square floating beside it. The height
      // is whichever is smaller: the image cap, or the height at which both
      // widths plus the gap still fit across the content width.
      const widthLimited =
        Math.max(0, maxWidth - PS_MEDIA_GAP) / (imageRatio + qrRatio);
      const rowHeight = Math.min(PS_IMAGE_MAX_HEIGHT, widthLimited);
      image = { width: rowHeight * imageRatio, height: rowHeight };
      qr = { width: rowHeight * qrRatio, height: rowHeight };
    } else if (hasImage) {
      let w = maxWidth;
      let h = maxWidth / imageRatio;
      if (h > PS_IMAGE_MAX_HEIGHT) {
        h = PS_IMAGE_MAX_HEIGHT;
        w = PS_IMAGE_MAX_HEIGHT * imageRatio;
      }
      image = { width: w, height: h };
    } else if (hasQr) {
      // No image to match, so fall back to a modest standalone square
      let w = PS_QR_SIZE;
      let h = PS_QR_SIZE / qrRatio;
      if (h > PS_QR_SIZE) {
        h = PS_QR_SIZE;
        w = PS_QR_SIZE * qrRatio;
      }
      qr = { width: Math.min(w, maxWidth), height: h };
    }

    const height = Math.max(image ? image.height : 0, qr ? qr.height : 0);
    const rowWidth =
      (image ? image.width : 0) +
      (qr ? qr.width : 0) +
      (image && qr ? PS_MEDIA_GAP : 0);

    return {
      image: image,
      qr: qr,
      height: height,
      // Centred as a pair. Both sides are capped by height, so their widths
      // vary with aspect ratio — anchoring the QR to the right edge instead
      // would strand it across a wide gap from a portrait image.
      offsetX: Math.max(0, (maxWidth - rowWidth) / 2),
    };
  }

  /**
   * How many lines of `lineHeight` still fit between `y` and `maxContentY`.
   */
  function linesRemaining(y, lineHeight, maxContentY) {
    if (!lineHeight || lineHeight <= 0) return 0;
    return Math.floor((maxContentY - y) / lineHeight);
  }

  /**
   * Whether an atomic block should start on a fresh page.
   *
   * Only breaks when a break actually helps: never when we're already at the
   * top of a page, and never when the block is taller than a whole page
   * (it has to overflow wherever it starts, so breaking just wastes a page).
   */
  function shouldBreakForBlock(y, blockHeight, topMargin, maxContentY) {
    if (y > maxContentY) return true; // already past the content area
    if (y <= topMargin) return false; // at page top — nothing to gain
    if (blockHeight > maxContentY - topMargin) return false; // taller than a page
    return y + blockHeight > maxContentY;
  }

  /**
   * Paginate pre-measured lines, calling `drawLine(line, y)` for each.
   * Applies orphan/widow control so blocks split at sensible points.
   */
  function paintLines(pdf, lines, y, lineHeight, pageBreakConfig, drawLine) {
    if (!lines.length) return y;

    if (!pageBreakConfig) {
      lines.forEach(function (line) {
        drawLine(line, y);
        y += lineHeight;
      });
      return y;
    }

    const maxContentY = pageBreakConfig.maxContentY;
    const topMargin = pageBreakConfig.topMargin;
    let index = 0;

    while (index < lines.length) {
      const remaining = lines.length - index;
      const atTop = y <= topMargin;
      const room = linesRemaining(y, lineHeight, maxContentY);
      let take = Math.max(0, Math.min(room, remaining));

      // Widow control: never strand fewer than MIN_WIDOW_LINES on the next page.
      if (remaining - take > 0 && remaining - take < MIN_WIDOW_LINES) {
        take = remaining - MIN_WIDOW_LINES;
      }

      // Orphan control: if too little of the block would sit here, move it on.
      if (!atTop && (take < 1 || (remaining > take && take < MIN_ORPHAN_LINES))) {
        pdf.addPage();
        y = topMargin;
        continue; // next pass runs with atTop = true, so this can't loop
      }

      if (take < 1) take = 1; // page shorter than a line — draw anyway

      for (let i = 0; i < take; i++) {
        drawLine(lines[index + i], y);
        y += lineHeight;
      }
      index += take;
    }

    return y;
  }

  /**
   * Add word-wrapped text segments to PDF, rendering linked segments as
   * clickable hyperlinks. Same page-break behaviour as addWrappedText.
   */
  function addSegmentsWithLinks(
    pdf,
    segments,
    x,
    y,
    maxWidth,
    lineHeight,
    pageBreakConfig
  ) {
    // Layout pass — break the segments into lines of positioned runs without
    // drawing, so the paint pass can paginate them with orphan/widow control.
    const lines = layoutSegmentLines(pdf, segments, x, maxWidth);
    if (!lines.length) return y;

    // Paint pass
    return paintLines(pdf, lines, y, lineHeight, pageBreakConfig, function (
      runs,
      lineY
    ) {
      runs.forEach(function (run) {
        if (run.pill) {
          drawPillRun(pdf, run, lineY);
        } else if (run.url) {
          pdf.setTextColor(5, 99, 193); // Link blue
          pdf.textWithLink(run.text, run.x, lineY, { url: run.url });
          pdf.setDrawColor(5, 99, 193);
          pdf.setLineWidth(0.2);
          pdf.line(run.x, lineY + 0.7, run.x + run.width, lineY + 0.7);
        } else {
          pdf.setTextColor(102, 102, 102);
          pdf.text(run.text, run.x, lineY);
        }
      });
    });
  }

  /**
   * Wrap text segments into lines of positioned runs at the current font
   * size. Also used to measure a block's height before placing it.
   */
  function layoutSegmentLines(pdf, segments, x, maxWidth) {
    const spaceWidth = pdf.getTextWidth(" ");
    const lines = [];
    let line = [];
    let cursorX = x;

    function wrapLine() {
      if (line.length) lines.push(line);
      line = [];
      cursorX = x;
    }

    segments.forEach(function (segment) {
      if (segment.lineBreak) {
        wrapLine();
        if (segment.gap && lines.length) lines.push([]);
        return;
      }
      if (segment.pill) {
        pdf.setFont(undefined, "bold");
        const pillWidth = pdf.getTextWidth(segment.text) + PILL_PADDING_X * 2;
        pdf.setFont(undefined, "normal");
        if (cursorX > x && cursorX + pillWidth > x + maxWidth) {
          wrapLine();
        }
        line.push({ text: segment.text, x: cursorX, width: pillWidth, url: null, pill: segment.pill });
        cursorX += pillWidth + spaceWidth + 1; // a little air after the pill
        return;
      }
      const words = segment.text.split(/\s+/).filter(Boolean);
      let wordIndex = 0;

      while (wordIndex < words.length) {
        // Build the longest run of words that fits on the current line, so
        // linked text gets a single continuous underline per line
        let run = words[wordIndex];
        let runWidth = pdf.getTextWidth(run);

        // Wrap first if even one word doesn't fit here (but fits on a fresh line)
        if (cursorX > x && cursorX + runWidth > x + maxWidth) {
          wrapLine();
        }

        let nextIndex = wordIndex + 1;
        while (nextIndex < words.length) {
          const candidate = run + " " + words[nextIndex];
          const candidateWidth = pdf.getTextWidth(candidate);
          if (cursorX + candidateWidth > x + maxWidth) {
            break;
          }
          run = candidate;
          runWidth = candidateWidth;
          nextIndex++;
        }

        line.push({
          text: run,
          x: cursorX,
          width: runWidth,
          url: segment.url,
        });

        cursorX += runWidth + spaceWidth;
        wordIndex = nextIndex;
      }
    });
    wrapLine();
    return lines;
  }

  /**
   * Extract HTML content (for rich text)
   */
  function extractHtmlContent(element) {
    if (!element) return "";

    // Clone to avoid modifying original
    const clone = element.cloneNode(true);

    // Remove script and style elements
    const scripts = clone.querySelectorAll("script, style");
    scripts.forEach((el) => el.remove());

    // Remove interactive elements
    const interactive = clone.querySelectorAll(
      "button, input, select, textarea"
    );
    interactive.forEach((el) => el.remove());

    return clone.innerHTML || "";
  }

  /**
   * Load every <img> referenced in an HTML string so renderHtmlToPDF (which
   * is synchronous) can draw them. Resolves with a map of src attribute ->
   * { data, width, height } where data is a PNG data URL. Images that fail
   * to load are left out so the renderer skips them and keeps going.
   */
  // Content images are drawn at most 160mm wide, so anything beyond twice
  // the capture width only inflates the PDF.
  const MAX_CONTENT_IMAGE_PX = CAPTURE_LAYOUT_WIDTH * 2;
  const CONTENT_IMAGE_TIMEOUT_MS = 15000;

  function preloadHtmlImages(html) {
    if (!html || !/<img[\s>]/i.test(html)) {
      return Promise.resolve({});
    }

    const doc = new DOMParser().parseFromString(html, "text/html");
    const sources = [];
    doc.querySelectorAll("img[src]").forEach(function (img) {
      const src = img.getAttribute("src");
      if (src && sources.indexOf(src) === -1) {
        sources.push(src);
      }
    });

    return Promise.all(
      sources.map(function (src) {
        return new Promise(function (resolve) {
          const img = new Image();
          let settled = false;
          const finish = function (result) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(result);
          };
          // A server that accepts the connection but never answers would
          // otherwise leave the export spinning forever.
          const timer = setTimeout(function () {
            console.error("Timed out loading content image:", src);
            finish(null);
          }, CONTENT_IMAGE_TIMEOUT_MS);

          img.crossOrigin = "anonymous";
          img.onload = function () {
            try {
              // Downscale large images; keep JPEG sources as JPEG so photos
              // aren't re-encoded as much larger PNGs.
              const scale = Math.min(1, MAX_CONTENT_IMAGE_PX / img.naturalWidth);
              const canvas = document.createElement("canvas");
              canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
              canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
              canvas
                .getContext("2d")
                .drawImage(img, 0, 0, canvas.width, canvas.height);
              const isJpeg = /\.jpe?g($|[?#])/i.test(src);
              finish({
                src: src,
                data: isJpeg
                  ? canvas.toDataURL("image/jpeg", 0.9)
                  : canvas.toDataURL("image/png"),
                format: isJpeg ? "JPEG" : "PNG",
                // Natural size, so layout maths matches what the page shows
                width: img.naturalWidth,
                height: img.naturalHeight,
              });
            } catch (error) {
              console.error("Error processing content image:", src, error);
              finish(null);
            }
          };
          img.onerror = function () {
            console.error("Error loading content image:", src);
            finish(null);
          };
          img.src = src;
        });
      })
    ).then(function (results) {
      const images = {};
      results.forEach(function (result) {
        if (result) {
          images[result.src] = result;
        }
      });
      return images;
    });
  }

  // Line height of body text rendered from HTML (text blocks).
  const HTML_BASE_LINE_HEIGHT = 7;

  // Heading styles for text blocks and title items, so both look the same.
  // lineGap is added to the line height; before/after are spacing (mm).
  // h5/h6 use h4.
  const HEADING_STYLES = {
    h1: { fontSize: 16, lineGap: 3, before: 2, after: 3 },
    h2: { fontSize: 14, lineGap: 2, before: 2, after: 2 },
    h3: { fontSize: 12, lineGap: 1, before: 1, after: 2 },
    h4: { fontSize: 11, lineGap: 1, before: 1, after: 1 },
  };

  function headingStyle(tagName) {
    return HEADING_STYLES[tagName] || HEADING_STYLES.h4;
  }

  /**
   * Height a heading takes when drawn with drawHeading.
   */
  function measureHeading(pdf, tagName, text, maxWidth) {
    const style = headingStyle(tagName);
    pdf.setFontSize(style.fontSize);
    pdf.setFont(undefined, "bold");
    const lineCount = splitTextIntoLines(pdf, text, maxWidth).length;
    pdf.setFont(undefined, "normal");
    return (
      style.before +
      lineCount * (HTML_BASE_LINE_HEIGHT + style.lineGap) +
      style.after
    );
  }

  /**
   * Draw a heading and return the y position below it.
   */
  function drawHeading(pdf, tagName, text, x, y, maxWidth, pageBreakConfig) {
    const style = headingStyle(tagName);
    pdf.setTextColor(30, 51, 86);
    pdf.setFontSize(style.fontSize);
    pdf.setFont(undefined, "bold");
    y = addWrappedText(
      pdf,
      text,
      x,
      y + style.before,
      maxWidth,
      HTML_BASE_LINE_HEIGHT + style.lineGap,
      pageBreakConfig
    );
    pdf.setFont(undefined, "normal");
    return y + style.after;
  }

  /**
   * Render HTML content to PDF with formatting.
   * `images` is the map from preloadHtmlImages(html); without it any <img>
   * in the content is skipped.
   */
  function renderHtmlToPDF(
    pdf,
    html,
    x,
    y,
    maxWidth,
    pageHeight,
    margin,
    footerSpace,
    images
  ) {
    if (!html) return y;
    images = images || {};

    // Wrap in a container if needed
    const wrappedHtml = html.trim().startsWith("<")
      ? html
      : "<div>" + html + "</div>";
    const parser = new DOMParser();
    const doc = parser.parseFromString(wrappedHtml, "text/html");
    const body = doc.body;

    let currentY = y;
    const baseLineHeight = HTML_BASE_LINE_HEIGHT;
    const baseFontSize = 10;
    let listItemCounter = 0;
    const maxContentY = pageHeight - margin - footerSpace; // Bottom margin + footer space
    const pageBreakConfig = { maxContentY: maxContentY, topMargin: margin };
    // Text is drawn with currentY as its baseline, so leave a full line below
    // an image for the next line's glyphs to sit in.
    const imageSpacing = baseLineHeight;

    // A width/height attribute in pixels, or NaN for anything else (e.g.
    // "100%", which parseFloat would otherwise read as 100px).
    function pixelAttribute(node, name) {
      const value = (node.getAttribute(name) || "").trim();
      return /^\d+(\.\d+)?(px)?$/i.test(value) ? parseFloat(value) : NaN;
    }

    // Size an <img> the way the report builder shows it: its width/height
    // attributes if set, otherwise its natural size, converted at 96 DPI and
    // shrunk to fit the available width and a single page of content.
    function imageLayout(node, indent) {
      const src = node.getAttribute("src");
      const loaded = src ? images[src] : null;
      if (!loaded || !loaded.width || !loaded.height) return null;

      const mmPerPixel = 0.264583;
      const attrWidth = pixelAttribute(node, "width");
      const attrHeight = pixelAttribute(node, "height");
      const widthPx = attrWidth > 0 ? attrWidth : loaded.width;
      const heightPx =
        attrWidth > 0 && attrHeight > 0
          ? attrHeight
          : widthPx * (loaded.height / loaded.width);

      let width = widthPx * mmPerPixel;
      let height = heightPx * mmPerPixel;

      const availableWidth = maxWidth - indent;
      if (width > availableWidth) {
        height = height * (availableWidth / width);
        width = availableWidth;
      }
      const availableHeight = maxContentY - margin;
      if (height > availableHeight) {
        width = width * (availableHeight / height);
        height = availableHeight;
      }

      return {
        data: loaded.data,
        format: loaded.format || "PNG",
        width: width,
        height: height,
      };
    }

    function drawImage(node, indent) {
      const layout = imageLayout(node, indent);
      if (!layout) {
        // Same marker the placed-image items use, so the reader can tell an
        // image belonged here rather than it silently vanishing.
        if (node.getAttribute("src")) {
          pdf.setFontSize(baseFontSize);
          pdf.setTextColor(102, 102, 102);
          pdf.setFont(undefined, "italic");
          currentY = addWrappedText(
            pdf,
            "[Image - Unable to load]",
            x + indent,
            currentY,
            maxWidth - indent,
            baseLineHeight,
            pageBreakConfig
          );
          pdf.setFont(undefined, "normal");
        }
        return;
      }

      if (shouldBreakForBlock(currentY, layout.height, margin, maxContentY)) {
        pdf.addPage();
        currentY = margin;
      }

      pdf.addImage(
        layout.data,
        layout.format,
        x + indent,
        currentY,
        layout.width,
        layout.height
      );
      // A linked image stays clickable in the PDF
      const url = linkUrlFor(node);
      if (url) {
        pdf.link(x + indent, currentY, layout.width, layout.height, { url: url });
      }
      currentY += layout.height + imageSpacing;
    }

    // The absolute URL of the nearest enclosing <a href>, or "" if none.
    // Resolved against the page since the HTML was parsed out of context.
    function linkUrlFor(node) {
      const anchor = node.closest ? node.closest("a[href]") : null;
      if (!anchor) return "";
      try {
        const url = new URL(anchor.getAttribute("href"), window.location.href);
        return /^(https?|mailto|tel):$/.test(url.protocol) ? url.href : "";
      } catch (e) {
        return "";
      }
    }

    function estimateImagesHeight(node, indent) {
      let total = 0;
      node.querySelectorAll("img").forEach(function (img) {
        const layout = imageLayout(img, indent);
        if (layout) total += layout.height + imageSpacing;
      });
      return total;
    }

    function estimateElementHeight(node, indent = 0) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (!text) return 0;

        pdf.setFontSize(baseFontSize);
        const lines = splitTextIntoLines(pdf, text, maxWidth - indent);
        return lines.length * baseLineHeight;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        if (tagName === "img") {
          const layout = imageLayout(node, indent);
          return layout ? layout.height + imageSpacing : 0;
        }

        const imagesHeight = estimateImagesHeight(node, indent);
        const text = node.textContent.trim();
        if (!text) return imagesHeight;

        switch (tagName) {
          case "h1":
          case "h2":
          case "h3":
          case "h4":
          case "h5":
          case "h6":
            return measureHeading(pdf, tagName, text, maxWidth);
          case "p":
            pdf.setFontSize(baseFontSize);
            const pLines = splitTextIntoLines(pdf, text, maxWidth - indent);
            return 1 + pLines.length * baseLineHeight + 2 + imagesHeight;
          case "ul":
          case "ol":
            const items = Array.from(node.children).filter(
              (li) => li.tagName.toLowerCase() === "li"
            );
            let listHeight = 1; // Initial spacing
            items.forEach(function (li) {
              pdf.setFontSize(baseFontSize);
              const liLines = splitTextIntoLines(
                pdf,
                li.textContent.trim(),
                maxWidth - indent - 5
              );
              listHeight += liLines.length * baseLineHeight + 1;
            });
            return listHeight + 1; // Final spacing
          case "br":
            return baseLineHeight;
          default:
            // Rough estimate for other elements
            pdf.setFontSize(baseFontSize);
            const defaultLines = splitTextIntoLines(
              pdf,
              text,
              maxWidth - indent
            );
            return defaultLines.length * baseLineHeight + imagesHeight;
        }
      }
      return 0;
    }

    // Tags whose content flows and can safely split across pages. Anything
    // else is atomic and gets moved whole (when it fits on one page).
    const FLOWABLE_TAGS = [
      "p",
      "ul",
      "ol",
      "div",
      "blockquote",
      "section",
      "article",
      "figure",
      "table",
      "span",
    ];

    // Draw one list item as inline content, so its links and bold/italic
    // come through, with the bullet or number in front.
    function renderListItem(li, marker, indent) {
      const item = doc.createElement("span");
      item.appendChild(doc.createTextNode(marker));
      Array.from(li.childNodes).forEach(function (child) {
        item.appendChild(child.cloneNode(true));
      });
      // Match the old li.textContent.trim(): no stray whitespace around the text
      const walker = doc.createTreeWalker(item, NodeFilter.SHOW_TEXT);
      const texts = [];
      while (walker.nextNode()) texts.push(walker.currentNode);
      if (texts.length > 1) {
        texts[1].textContent = texts[1].textContent.replace(/^\s+/, "");
      }
      const last = texts[texts.length - 1];
      last.textContent = last.textContent.replace(/\s+$/, "");
      currentY = processInlineContent(item, x + indent + 5, maxWidth - indent - 5);
    }

    function processNode(node, indent = 0, parentTag = "") {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) {
          // Free text flows — addWrappedText paginates it line by line.
          pdf.setFontSize(baseFontSize);
          pdf.setTextColor(102, 102, 102);
          pdf.setFont(undefined, "normal");
          currentY = addWrappedText(
            pdf,
            text,
            x + indent,
            currentY,
            maxWidth - indent,
            baseLineHeight,
            pageBreakConfig
          );
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        const isHeading = /^h[1-6]$/.test(tagName);
        const isFlowable = FLOWABLE_TAGS.indexOf(tagName) !== -1;

        if (currentY > maxContentY) {
          // Already past the content area
          pdf.addPage();
          currentY = margin;
        } else if (isHeading) {
          // Keep-with-next: a heading takes the first couple of lines of its
          // content with it, so it never strands at the foot of a page. When
          // an image follows, it takes the whole image instead.
          let next = node.nextSibling;
          while (
            next &&
            next.nodeType === Node.TEXT_NODE &&
            !next.textContent.trim()
          ) {
            next = next.nextSibling;
          }
          const nextHasImage =
            next &&
            next.nodeType === Node.ELEMENT_NODE &&
            (next.tagName.toLowerCase() === "img" || next.querySelector("img"));
          const keepWith = nextHasImage
            ? estimateElementHeight(next, indent)
            : baseLineHeight * 2;
          const needed = estimateElementHeight(node, indent) + keepWith;
          if (shouldBreakForBlock(currentY, needed, margin, maxContentY)) {
            pdf.addPage();
            currentY = margin;
          }
        } else if (!isFlowable) {
          const needed = estimateElementHeight(node, indent);
          if (shouldBreakForBlock(currentY, needed, margin, maxContentY)) {
            pdf.addPage();
            currentY = margin;
          }
        }
        // Flowable elements are never moved wholesale: they fill the rest of
        // the current page and split as they render.

        switch (tagName) {
          case "h1":
          case "h2":
          case "h3":
          case "h4":
          case "h5":
          case "h6":
            currentY = drawHeading(
              pdf,
              tagName,
              node.textContent.trim(),
              x,
              currentY,
              maxWidth,
              pageBreakConfig
            );
            break;

          case "img":
            drawImage(node, indent);
            break;

          case "p":
            currentY += 1;
            pdf.setTextColor(102, 102, 102); // Set gray for paragraph text
            if (node.querySelector("img")) {
              // Images can't sit inside a wrapped text line, so render the
              // paragraph as runs of inline text with each image drawn as a
              // block between them (in document order).
              let pending = doc.createElement("span");
              const flushPending = function () {
                if (pending.textContent.trim()) {
                  currentY = processInlineContent(
                    pending,
                    x + indent,
                    maxWidth - indent
                  );
                }
                pending = doc.createElement("span");
              };
              Array.from(node.childNodes).forEach(function (child) {
                const hasImage =
                  child.nodeType === Node.ELEMENT_NODE &&
                  (child.tagName.toLowerCase() === "img" ||
                    child.querySelector("img"));
                if (hasImage) {
                  flushPending();
                  processNode(child, indent, tagName);
                } else {
                  pending.appendChild(child.cloneNode(true));
                }
              });
              flushPending();
            } else {
              // Process paragraph content with inline formatting
              // processInlineContent returns the final Y position
              currentY = processInlineContent(node, x + indent, maxWidth - indent);
            }
            currentY += 2; // Add spacing after paragraph
            break;

          case "ul":
            currentY += 1;
            listItemCounter = 0;
            Array.from(node.children).forEach(function (li) {
              if (li.tagName.toLowerCase() === "li") {
                renderListItem(li, "• ", indent);
                currentY += 1;
              }
            });
            currentY += 1;
            break;

          case "ol":
            currentY += 1;
            listItemCounter = 0;
            Array.from(node.children).forEach(function (li) {
              if (li.tagName.toLowerCase() === "li") {
                listItemCounter++;
                renderListItem(li, listItemCounter + ". ", indent);
                currentY += 1;
              }
            });
            currentY += 1;
            break;

          case "li":
            // Handled by parent ul/ol
            break;

          case "br":
            currentY += baseLineHeight;
            break;

          case "div":
            // Process children recursively
            Array.from(node.childNodes).forEach(function (child) {
              processNode(child, indent, tagName);
            });
            break;

          default:
            // For other tags, process their children
            Array.from(node.childNodes).forEach(function (child) {
              processNode(child, indent, tagName);
            });
        }
      }
    }

    function processInlineContent(node, startX, width) {
      // Extract text segments with their formatting
      const segments = [];

      function extractSegments(node, isBold, isItalic) {
        Array.from(node.childNodes).forEach(function (child) {
          if (child.nodeType === Node.TEXT_NODE) {
            const text = child.textContent;
            if (text) {
              segments.push({
                text: text,
                bold: isBold,
                italic: isItalic,
                url: linkUrlFor(child.parentNode),
              });
            }
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const childTag = child.tagName.toLowerCase();
            if (childTag === "strong" || childTag === "b") {
              extractSegments(child, true, isItalic);
            } else if (childTag === "em" || childTag === "i") {
              extractSegments(child, isBold, true);
            } else {
              extractSegments(child, isBold, isItalic);
            }
          }
        });
      }

      extractSegments(node, false, false);

      function fontStyleFor(segment) {
        if (segment.bold && segment.italic) return "bolditalic";
        if (segment.bold) return "bold";
        if (segment.italic) return "italic";
        return "normal";
      }

      // Layout pass — wrap the segments into lines of positioned runs,
      // measuring each run under its own font so widths stay accurate.
      // Nothing is drawn here, so the paragraph can be paginated afterwards
      // instead of being moved to a fresh page in one piece.
      pdf.setFontSize(baseFontSize);
      const lines = [];
      let line = [];
      let currentX = startX;

      segments.forEach(function (segment) {
        const style = fontStyleFor(segment);
        pdf.setFont(undefined, style);

        // Split text into words, keeping whitespace
        segment.text.split(/(\s+)/).forEach(function (word) {
          if (!word) return;

          const wordWidth = pdf.getTextWidth(word);

          // Check if word fits on current line
          if (currentX + wordWidth > startX + width && currentX > startX) {
            lines.push(line);
            line = [];
            currentX = startX;
            if (/^\s+$/.test(word)) return; // don't open a line with a space
          }

          line.push({
            text: word,
            x: currentX,
            width: wordWidth,
            style: style,
            url: segment.url,
          });
          currentX += wordWidth;
        });
      });
      if (line.length) lines.push(line);

      pdf.setFont(undefined, "normal");
      if (!lines.length) return currentY;

      // Paint pass
      pdf.setFontSize(baseFontSize);
      pdf.setTextColor(102, 102, 102);
      const endY = paintLines(
        pdf,
        lines,
        currentY,
        baseLineHeight,
        pageBreakConfig,
        function (runs, lineY) {
          runs.forEach(function (run) {
            pdf.setFont(undefined, run.style);
            if (run.url) {
              // Same link styling as addSegmentsWithLinks
              pdf.setTextColor(5, 99, 193);
              pdf.textWithLink(run.text, run.x, lineY, { url: run.url });
              pdf.setDrawColor(5, 99, 193);
              pdf.setLineWidth(0.2);
              pdf.line(run.x, lineY + 0.7, run.x + run.width, lineY + 0.7);
              pdf.setTextColor(102, 102, 102);
            } else {
              pdf.text(run.text, run.x, lineY);
            }
          });
        }
      );

      // Reset font
      pdf.setFont(undefined, "normal");

      // Return the final Y position
      return endY;
    }

    // Process all child nodes
    Array.from(body.childNodes).forEach(function (node) {
      processNode(node);
    });

    return currentY;
  }

  /**
   * Get title from item header
   */
  function getItemTitle(item) {
    // Try different title selectors for different item types
    const titleSelectors = [
      ".chart-item-title h4",
      ".text-item-title h4",
      ".faq-item-title h4",
      ".image-item-title h4",
      ".calculator-item-title h4",
      ".patient-story-item-title h4",
      ".title-text",
    ];

    for (const selector of titleSelectors) {
      const titleEl = item.querySelector(selector);
      if (titleEl) {
        return extractTextContent(titleEl).trim();
      }
    }

    return "";
  }

  /**
   * Get content from item based on type
   * Returns object with type and content/image data
   */
  function getItemContent(item) {
    const itemType = item.className;
    let result = {
      type: "text",
      content: "",
      imageData: null,
      imageWidth: 0,
      imageHeight: 0,
      qrCodeUrl: null,
      qrCodeWidth: 0,
      qrCodeHeight: 0,
      aiSummary: null,
      recommendationRole: null,
      recommendationSegments: null,
      recommendationCompact: false,
    };

    // Chart items - capture as image
    if (itemType.includes("canvas-chart-item")) {
      result.type = "image";

      // Extract AI summary if present and visible
      const aiSummary = item.querySelector(".chart-ai-summary");
      if (
        aiSummary &&
        aiSummary.style.display !== "none" &&
        aiSummary.style.display !== ""
      ) {
        // Check for summary text content
        const summaryText = aiSummary.querySelector(".chart-ai-summary-text");
        if (summaryText) {
          result.aiSummary = extractTextContent(summaryText);
        } else {
          // If no specific text element, get all text content excluding loading/error messages
          const loadingEl = aiSummary.querySelector(
            ".chart-ai-summary-loading"
          );
          const errorEl = aiSummary.querySelector(".chart-ai-summary-error");
          if (!loadingEl && !errorEl) {
            result.aiSummary = extractTextContent(aiSummary);
          }
        }
      }

      // Extract recommendation (role + text with links) if present
      const recommendation = item.querySelector(".chart-recommendation");
      if (recommendation) {
        const roleEl = recommendation.querySelector(
          ".chart-recommendation-role"
        );
        const recTextEl = recommendation.querySelector(
          ".chart-recommendation-text"
        );
        result.recommendationRole = roleEl
          ? extractTextContent(roleEl).trim()
          : null;
        result.recommendationSegments = recTextEl
          ? extractTextSegmentsWithLinks(recTextEl)
          : null;
        // data-pdf-compact: smaller text, kept on the same page as the chart
        result.recommendationCompact =
          recommendation.hasAttribute("data-pdf-compact");
      }

      // Will be captured separately
      return result;
    }
    // Calculator items - capture as image
    else if (itemType.includes("canvas-calculator-item")) {
      result.type = "image";
      // Will be captured separately
      return result;
    }
    // Text items
    else if (itemType.includes("canvas-text-item")) {
      const contentArea = item.querySelector(".text-item-content");
      if (contentArea) {
        // Check for WYSIWYG editor content
        const wysiwyg = contentArea.querySelector(".wysiwyg-textarea");
        if (wysiwyg) {
          result.content = wysiwyg.value || extractTextContent(contentArea);
        } else {
          // Try to get content from iframe (TinyMCE)
          const iframe = contentArea.querySelector("iframe");
          if (iframe && iframe.contentDocument) {
            const body = iframe.contentDocument.body;
            result.content = extractTextContent(body);
          } else {
            result.content = extractTextContent(contentArea);
          }
        }
      }
    }
    // FAQ items
    else if (itemType.includes("canvas-faq-item")) {
      const contentArea = item.querySelector(".faq-item-content");
      if (contentArea) {
        const answerContent = contentArea.querySelector(".faq-answer-content");
        if (answerContent) {
          // Preserve HTML formatting for FAQ content
          result.content = extractHtmlContent(answerContent);
        } else {
          result.content = extractHtmlContent(contentArea);
        }
      }
    }
    // Image items
    else if (itemType.includes("canvas-image-item")) {
      const img = item.querySelector("img");

      if (img && img.src) {
        result.type = "image";
        result.imageData = img.src;
        result.imageWidth = img.naturalWidth || img.width || 0;
        result.imageHeight = img.naturalHeight || img.height || 0;
        // Display size as it would be on a desktop screen (natural size,
        // capped at the capture width) so it doesn't shrink on small screens
        if (result.imageWidth > 0 && result.imageHeight > 0) {
          result.maxWidth = Math.min(result.imageWidth, CAPTURE_LAYOUT_WIDTH);
          result.maxHeight =
            result.imageHeight * (result.maxWidth / result.imageWidth);
        } else {
          result.maxWidth = img.width;
          result.maxHeight = img.height;
        }
      } else {
        result.content = "[Image]";
      }
    }
    // Patient story items
    else if (itemType.includes("canvas-patient-story-item")) {
      const contentArea = item.querySelector(".patient-story-item-content");
      if (contentArea) {
        // Check for featured image
        const featuredImageDiv = contentArea.querySelector(
          ".patient-story-featured-image"
        );
        if (featuredImageDiv) {
          const featuredImg = featuredImageDiv.querySelector("img");
          if (featuredImg && featuredImg.src) {
            result.imageData = featuredImg.src;
            result.imageWidth =
              featuredImg.naturalWidth || featuredImg.width || 0;
            result.imageHeight =
              featuredImg.naturalHeight || featuredImg.height || 0;
          }
        }

        // Extract text content (excerpt)
        const excerptArea = contentArea.querySelector(".patient-story-excerpt");
        if (excerptArea) {
          result.content = extractTextContent(excerptArea);
        } else {
          result.content = extractTextContent(contentArea);
        }

        // Check for QR code image
        const qrCodeDiv = contentArea.querySelector(".patient-story-qr-code");
        if (qrCodeDiv) {
          const qrImg = qrCodeDiv.querySelector("img");
          if (qrImg && qrImg.src) {
            result.qrCodeUrl = qrImg.src;
            result.qrCodeWidth = qrImg.naturalWidth || qrImg.width || 0;
            result.qrCodeHeight = qrImg.naturalHeight || qrImg.height || 0;
          }
        }
      }
    }

    result.content = result.content.trim();
    return result;
  }

  /**
   * Extract report header information
   */
  function extractReportHeader(canvasCharts) {
    const header = {
      hospitalName: "",
      department: "",
      subheading: "",
      ward: "",
      hospitalLogoUrl: "",
      reportName: "",
      reportDescription: "",
      reportDate: "",
    };

    const headerEl = canvasCharts.querySelector(".canvas-report-header");
    if (!headerEl) return header;

    // Hospital info
    const hospitalInfo = headerEl.querySelector(
      ".canvas-hospital-info-content"
    );
    if (hospitalInfo) {
      const h4 = hospitalInfo.querySelector("h4");
      if (h4) header.hospitalName = extractTextContent(h4);

      const department = hospitalInfo.querySelector(".canvas-hospital-department");
      if (department) header.department = extractTextContent(department).trim();

      const subheading = hospitalInfo.querySelector(".canvas-report-subheading");
      if (subheading) header.subheading = extractTextContent(subheading).trim();

      // Ward selector renders as plain text; "None" (empty value) prints nothing.
      const wardSelect = hospitalInfo.querySelector(".canvas-report-ward-select");
      if (wardSelect) header.ward = wardSelect.value.trim();
    }

    // Hospital logo
    const logoImg = headerEl.querySelector(".canvas-hospital-info-image");
    if (logoImg && logoImg.src) {
      header.hospitalLogoUrl = logoImg.src;
    }

    // Report info
    const reportInfo = headerEl.querySelector(".canvas-report-info-content");
    if (reportInfo) {
      const h4 = reportInfo.querySelector("h4");
      if (h4) header.reportName = extractTextContent(h4);
    }

    // Report description (now outside of canvas-report-info-content)
    const reportDescription = headerEl.querySelector(
      ".canvas-report-description"
    );
    if (reportDescription) {
      header.reportDescription = extractTextContent(reportDescription);
    }

    // Report date
    const dateDisplay = headerEl.querySelector(".canvas-report-date-display");
    if (dateDisplay) {
      header.reportDate = extractTextContent(dateDisplay);
    }

    return header;
  }

  /**
   * Split text into lines that fit within PDF width
   */
  function splitTextIntoLines(pdf, text, maxWidth) {
    const words = text.split(" ");
    const lines = [];
    let currentLine = "";

    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const testLine = currentLine ? currentLine + " " + word : word;
      const testWidth = pdf.getTextWidth(testLine);

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  }

  /**
   * Add text to PDF with word wrapping.
   *
   * When `pageBreakConfig` is provided ({ maxContentY, topMargin }), the text
   * fills the current page and splits across pages with orphan/widow control,
   * so it never overlaps the footer and never strands a single line.
   */
  function addWrappedText(pdf, text, x, y, maxWidth, lineHeight, pageBreakConfig) {
    if (!text) return y;

    const lines = splitTextIntoLines(pdf, text, maxWidth);

    return paintLines(pdf, lines, y, lineHeight, pageBreakConfig, function (
      line,
      lineY
    ) {
      pdf.text(line, x, lineY);
    });
  }

  /**
   * Export canvas content to PDF
   */
  function exportToPDF() {
    const canvasCharts = document.getElementById("canvasCharts");

    if (!canvasCharts) {
      console.error("Canvas charts element not found");
      alert("Error: Report canvas not found.");
      return;
    }

    // Wait for libraries to be ready, then proceed
    waitForLibrariesReady(function (JsPDF) {
      performExport(canvasCharts, JsPDF);
    });
  }

  /**
   * Save canvas content to PDF and upload to hospital
   */
  function saveToPDF() {
    const canvasCharts = document.getElementById("canvasCharts");

    if (!canvasCharts) {
      console.error("Canvas charts element not found");
      alert("Error: Report canvas not found.");
      return;
    }

    // Wait for libraries to be ready, then proceed
    waitForLibrariesReady(function (JsPDF) {
      performSave(canvasCharts, JsPDF);
    });
  }

  /**
   * Render report header with logo and formatted text
   */
  function renderReportHeader(
    pdf,
    header,
    margin,
    pageWidth,
    maxWidth,
    pageHeight,
    lineHeight,
    titleLineHeight,
    sectionSpacing
  ) {
    return new Promise(function (resolve) {
      let headerY = margin;
      let leftColumnY = headerY;
      let rightColumnY = headerY;

      // Logo dimensions
      const hospitalLogoHeight = 25; // Hospital logo height

      // Calculate column widths
      const rightColumnWidth = 50; // Width reserved for right column (logo)
      const leftColumnWidth = maxWidth - rightColumnWidth - 10; // 10mm gap between columns

      let hospitalLogoLoaded = false;

      // LEFT COLUMN - hospital name, department, sub-heading.
      // Drawn first: with no logo, continueHeaderRendering runs immediately.
      // Starts one line down so the text lines up with the top of the logo.
      leftColumnY += lineHeight - 1;

      if (header.hospitalName) {
        pdf.setFontSize(12);
        pdf.setTextColor(30, 51, 86);
        pdf.setFont(undefined, "bold");
        leftColumnY = addWrappedText(
          pdf,
          header.hospitalName.toUpperCase(),
          margin,
          leftColumnY,
          leftColumnWidth,
          lineHeight
        );
        pdf.setFont(undefined, "normal");
        leftColumnY += 1;
      }

      if (header.department) {
        pdf.setFontSize(10);
        pdf.setTextColor(102, 102, 102);
        leftColumnY = addWrappedText(
          pdf,
          header.department,
          margin,
          leftColumnY,
          leftColumnWidth,
          lineHeight - 1
        );
      }

      if (header.subheading) {
        pdf.setFontSize(10);
        pdf.setTextColor(30, 51, 86);
        pdf.setFont(undefined, "bold");
        leftColumnY = addWrappedText(
          pdf,
          header.subheading,
          margin,
          leftColumnY,
          leftColumnWidth,
          lineHeight - 1
        );
        pdf.setFont(undefined, "normal");
      }

      if (header.ward) {
        pdf.setFontSize(10);
        pdf.setTextColor(102, 102, 102);
        leftColumnY = addWrappedText(
          pdf,
          header.ward,
          margin,
          leftColumnY,
          leftColumnWidth,
          lineHeight - 1
        );
      }

      // RIGHT COLUMN - Hospital logo at top right
      if (header.hospitalLogoUrl) {
        const hospitalLogo = new Image();
        hospitalLogo.crossOrigin = "anonymous";
        hospitalLogo.onload = function () {
          try {
            const aspectRatio = hospitalLogo.width / hospitalLogo.height;
            let logoWidth = hospitalLogoHeight * aspectRatio;
            let logoHeight = hospitalLogoHeight;

            // Scale if too wide
            if (logoWidth > rightColumnWidth) {
              logoWidth = rightColumnWidth;
              logoHeight = logoWidth / aspectRatio;
            }

            pdf.addImage(
              header.hospitalLogoUrl,
              "PNG",
              pageWidth - margin - logoWidth,
              rightColumnY,
              logoWidth,
              logoHeight
            );
            rightColumnY += logoHeight;

            hospitalLogoLoaded = true;
            continueHeaderRendering();
          } catch (error) {
            console.error("Error adding hospital logo:", error);
            hospitalLogoLoaded = true;
            continueHeaderRendering();
          }
        };

        hospitalLogo.onerror = function () {
          console.error("Error loading hospital logo");
          hospitalLogoLoaded = true;
          continueHeaderRendering();
        };

        hospitalLogo.src = header.hospitalLogoUrl;
      } else {
        hospitalLogoLoaded = true;
        continueHeaderRendering();
      }

      function continueHeaderRendering() {
        if (!hospitalLogoLoaded) return;

        // Use the maximum Y position from both columns
        let y = Math.max(leftColumnY, rightColumnY) + 3;

        // Add separator line
        pdf.setLineWidth(0.1);
        pdf.setDrawColor(222, 222, 222);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 10; // Increased margin below the line

        // Report name with date on the right
        const reportNameY = y;
        let hasReportName = false;

        if (header.reportName && header.reportName !== "Report Name") {
          hasReportName = true;
          // Report name on the left
          pdf.setFontSize(12);
          pdf.setTextColor(30, 51, 86);
          pdf.setFont(undefined, "bold");
          const reportNameWidth = maxWidth - 40; // Leave space for date
          y = addWrappedText(
            pdf,
            header.reportName,
            margin,
            reportNameY,
            reportNameWidth,
            lineHeight
          );
          pdf.setFont(undefined, "normal");
        }

        // Date on the right - always display if present
        if (header.reportDate) {
          pdf.setFontSize(9);
          pdf.setFont(undefined, "italic");
          pdf.setTextColor(102, 102, 102);
          const dateText = header.reportDate;
          const dateWidth = pdf.getTextWidth(dateText);
          const dateX = pageWidth - margin - dateWidth;
          pdf.text(dateText, dateX, reportNameY);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, "normal");

          // If there was no report name, advance Y position
          if (!hasReportName) {
            y += lineHeight;
          }
        }

        // Add spacing after report name/date section if either was present
        if (hasReportName || header.reportDate) {
          y += 3;
        }

        // Report description
        if (
          header.reportDescription &&
          header.reportDescription !== "Report Description"
        ) {
          pdf.setFontSize(10);
          pdf.setTextColor(102, 102, 102);
          y = addWrappedText(
            pdf,
            header.reportDescription,
            margin,
            y,
            maxWidth,
            lineHeight
          );
          y += 10;
        }

        resolve(y);
      }
    });
  }

  /**
   * Load the report background image as a JPEG data URL
   */
  function loadBackgroundImage() {
    return new Promise(function (resolve) {
      const themeUrl =
        window.location.origin + "/wp-content/themes/maxbizz-child";
      const bgUrl = themeUrl + "/assets/images/report-background.jpg";
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = function () {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/jpeg", 0.92));
        } catch (error) {
          console.error("Error processing background image:", error);
          resolve(null);
        }
      };
      img.onerror = function () {
        console.error("Error loading background image");
        resolve(null);
      };
      img.src = bgUrl;
    });
  }

  /**
   * Paint the background image on the current page and wrap addPage so every
   * subsequent page also gets the background drawn first (behind content).
   */
  function setupBackgroundForPDF(pdf, backgroundData, pageWidth, pageHeight) {
    if (!backgroundData) return;

    // First page already exists when the PDF is constructed
    pdf.addImage(backgroundData, "JPEG", 0, 0, pageWidth, pageHeight);

    const originalAddPage = pdf.addPage.bind(pdf);
    pdf.addPage = function () {
      const result = originalAddPage.apply(this, arguments);
      pdf.addImage(backgroundData, "JPEG", 0, 0, pageWidth, pageHeight);
      return result;
    };
  }

  // Footer logos, drawn left to right.
  const FOOTER_LOGO_FILES = [
    "footer-logo-just-say-no.png",
    "footer-logo-iimprove.png",
    "footer-logo-monash.png",
  ];

  /**
   * Load an image as PNG data: { data, width, height }, or null on failure
   * (the PDF is still generated without it).
   */
  function loadImageAsPngData(url) {
    return new Promise(function (resolve) {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = function () {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          canvas.getContext("2d").drawImage(img, 0, 0);
          resolve({
            data: canvas.toDataURL("image/png"),
            width: img.width,
            height: img.height,
          });
        } catch (error) {
          console.error("Error processing image:", url, error);
          resolve(null);
        }
      };

      img.onerror = function () {
        console.error("Error loading image:", url);
        resolve(null);
      };

      img.src = url;
    });
  }

  /**
   * Load the footer logos. Resolves with the ones that loaded, in order.
   */
  function loadFooterLogos() {
    const imagesUrl =
      window.location.origin + "/wp-content/themes/maxbizz-child/assets/images/";
    return Promise.all(
      FOOTER_LOGO_FILES.map(function (file) {
        return loadImageAsPngData(imagesUrl + file);
      })
    ).then(function (logos) {
      return logos.filter(Boolean);
    });
  }

  /**
   * Add footer to PDF page
   */
  function addPageFooter(
    pdf,
    pageNumber,
    totalPages,
    reportName,
    reportDate,
    pageWidth,
    pageHeight,
    margin,
    footerLogos
  ) {
    // Footer Y position (in the white content area, just above the border)
    const footerY = pageHeight - margin + 6; // Just above the bottom border
    const footerFontSize = 8;

    // Logos in a row, bottom left, same height
    const logoHeight = 10;
    const logoGap = 1.5;
    const logoBottomY = pageHeight - margin + 7;
    let logoX = margin;
    (footerLogos || []).forEach(function (logo) {
      try {
        const logoWidth = logoHeight * (logo.width / logo.height);
        pdf.addImage(
          logo.data,
          "PNG",
          logoX,
          logoBottomY - logoHeight,
          logoWidth,
          logoHeight
        );
        logoX += logoWidth + logoGap;
      } catch (error) {
        console.error("Error adding footer logo:", error);
      }
    });

    // Page number in bottom right (in white content area)
    pdf.setFontSize(footerFontSize);
    pdf.setFont(undefined, "normal");
    pdf.setTextColor(100, 100, 100);

    const pageText = pageNumber + " of " + totalPages;
    const pageTextWidth = pdf.getTextWidth(pageText);
    pdf.text(pageText, pageWidth - margin - pageTextWidth, footerY);

    // Reset text color
    pdf.setTextColor(0, 0, 0);
  }

  /**
   * Add image to PDF with proper sizing and page breaks
   */
  function addImageToPDF(
    pdf,
    imgData,
    imgWidth,
    imgHeight,
    x,
    y,
    maxWidth,
    pageHeight,
    margin,
    maxDisplayWidth,
    maxDisplayHeight,
    itemType,
    footerSpace
  ) {
    const bottomReserve = (typeof footerSpace === "number" ? footerSpace : 10);
    // Convert pixels to mm (assuming 96 DPI)
    const mmPerPixel = 0.264583;
    const imgWidthMM = imgWidth * mmPerPixel;
    const imgHeightMM = imgHeight * mmPerPixel;

    let scaledWidth, scaledHeight;

    // If max dimensions are provided (image items), render at the displayed
    // size in mm — i.e. the size the user actually sees in the report
    // builder. The previous cover logic could overflow the container when
    // image aspect ratio differed from the display container.
    if (maxDisplayWidth && maxDisplayHeight) {
      scaledWidth = maxDisplayWidth * mmPerPixel;
      scaledHeight = maxDisplayHeight * mmPerPixel;

      // Ensure we don't exceed page width
      if (scaledWidth > maxWidth) {
        const scale = maxWidth / scaledWidth;
        scaledWidth = maxWidth;
        scaledHeight = scaledHeight * scale;
      }
    } else {
      // Default behavior - different sizing for charts vs calculators
      let targetWidth;
      if (itemType === "chart") {
        targetWidth = maxWidth * 0.9; // Charts: 90% of page width
      } else if (itemType === "calculator") {
        // Calculators fill the content width, left-aligned with the text
        // around them, so their text comes out at body size (report builder
        // layout, see generateCalculatorHtml).
        targetWidth = maxWidth;
      } else {
        targetWidth = maxWidth * 0.8; // Default: 80% of page width
      }

      const scale = targetWidth / imgWidthMM;
      scaledWidth = imgWidthMM * scale;
      scaledHeight = imgHeightMM * scale;

      // Center the image between the margins (calculators stay left-aligned
      // with the text)
      x = itemType === "calculator" ? x : x + (maxWidth - scaledWidth) / 2;
    }

    // Check if image fits on current page (reserve footer space). An image
    // taller than a whole page gains nothing from a break, so don't burn a
    // page on it.
    const imageMaxY = pageHeight - margin - bottomReserve;
    if (shouldBreakForBlock(y, scaledHeight, margin, imageMaxY)) {
      pdf.addPage();
      y = margin;
    }

    // Add image
    pdf.addImage(imgData, "PNG", x, y, scaledWidth, scaledHeight);

    return y + scaledHeight;
  }

  /**
   * Perform the actual PDF export
   */
  function performExport(canvasCharts, JsPDF) {
    // Show loading indicator
    const originalButton = document.querySelector(
      '.toolbar-btn[data-action="export-pdf"]'
    );
    const originalLabel = originalButton
      ? originalButton.querySelector(".toolbar-btn-label").textContent
      : "Export PDF";

    if (originalButton) {
      originalButton.disabled = true;
      const labelElement = originalButton.querySelector(".toolbar-btn-label");
      if (labelElement) {
        labelElement.textContent = "Generating PDF...";
      }
    }

    try {
      // Create PDF
      const pdf = new JsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 25;
      const maxWidth = pageWidth - margin * 2;
      let y = margin;
      const lineHeight = 6;
      const titleLineHeight = 6;
      const sectionSpacing = 8;
      const footerSpace = 10; // Additional space above footer (logo and page numbers)

      // Extract report header
      const header = extractReportHeader(canvasCharts);

      // Load the background image first so it sits behind all content,
      // then render the header + content on top.
      loadBackgroundImage()
        .then(function (backgroundData) {
          setupBackgroundForPDF(pdf, backgroundData, pageWidth, pageHeight);

          // Render header with logo (async)
          return renderReportHeader(
            pdf,
            header,
            margin,
            pageWidth,
            maxWidth,
            pageHeight,
            lineHeight,
            titleLineHeight,
            sectionSpacing
          );
        })
        .then(function (startY) {
          // Extract all items including page breaks
          const items = canvasCharts.querySelectorAll(PDF_ITEM_SELECTOR);

          // Process items sequentially to handle async image capture
          return processItemsSequentially(
            items,
            pdf,
            margin,
            maxWidth,
            pageHeight,
            startY,
            lineHeight,
            titleLineHeight,
            sectionSpacing,
            footerSpace
          );
        })
        .then(loadFooterLogos)
        .then(function (footerLogos) {
          // Background is drawn per page via setupBackgroundForPDF.
          // Just add footers here.
          const totalPages = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            addPageFooter(
              pdf,
              i,
              totalPages,
              header.reportName,
              header.reportDate,
              pageWidth,
              pageHeight,
              margin,
              footerLogos
            );
          }

          // Generate filename with current date
          const now = new Date();
          const dateStr = now.toISOString().split("T")[0];
          const filename = `report-${dateStr}.pdf`;

          // Save PDF
          pdf.save(filename);

          // Restore button state
          if (originalButton) {
            originalButton.disabled = false;
            const labelElement =
              originalButton.querySelector(".toolbar-btn-label");
            if (labelElement) {
              labelElement.textContent = originalLabel;
            }
          }
        })
        .catch(function (error) {
          console.error("Error generating PDF:", error);
          alert("Error generating PDF. Please try again.");

          // Restore button state
          if (originalButton) {
            originalButton.disabled = false;
            const labelElement =
              originalButton.querySelector(".toolbar-btn-label");
            if (labelElement) {
              labelElement.textContent = originalLabel;
            }
          }
        });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");

      // Restore button state
      if (originalButton) {
        originalButton.disabled = false;
        const labelElement = originalButton.querySelector(".toolbar-btn-label");
        if (labelElement) {
          labelElement.textContent = originalLabel;
        }
      }
    }
  }

  // Canvas items included in the PDF
  const PDF_ITEM_SELECTOR = [
    ".canvas-chart-item",
    ".canvas-title-item",
    ".canvas-text-item",
    ".canvas-faq-item",
    ".canvas-image-item",
    ".canvas-calculator-item",
    ".canvas-patient-story-item",
    ".canvas-page-break-item",
  ].join(", ");

  /**
   * The part of a chart or calculator captured as an image: the chart
   * without its title and filters, or the calculator body.
   */
  function getCaptureElement(item) {
    if (item.classList.contains("canvas-chart-item")) {
      return item.querySelector(".chart-item-content") || item;
    }
    if (item.classList.contains("canvas-calculator-item")) {
      return (
        item.querySelector(".calculator-item-content .rb-calc") ||
        item.querySelector(".calculator-item-content") ||
        item
      );
    }
    return item;
  }

  // Font size and line height of compact chart recommendations
  const COMPACT_REC_FONT_SIZE = 7.5;
  const COMPACT_REC_LINE_HEIGHT = 3.6;

  function isCompactRecommendation(contentInfo) {
    return !!(
      contentInfo.recommendationCompact &&
      contentInfo.recommendationSegments &&
      contentInfo.recommendationSegments.length
    );
  }

  /**
   * Space (mm) kept on the same page below a chart image: the first two lines
   * of its AI summary or recommendation, or a compact recommendation in full.
   */
  function chartKeepWithNext(pdf, contentInfo, margin, maxWidth, lineHeight) {
    if (isCompactRecommendation(contentInfo)) {
      pdf.setFontSize(COMPACT_REC_FONT_SIZE);
      return (
        3 +
        layoutSegmentLines(
          pdf,
          contentInfo.recommendationSegments,
          margin,
          maxWidth
        ).length *
          COMPACT_REC_LINE_HEIGHT
      );
    }
    const hasFlowingCommentary =
      (contentInfo.aiSummary && contentInfo.aiSummary.trim()) ||
      (contentInfo.recommendationSegments &&
        contentInfo.recommendationSegments.length);
    return hasFlowingCommentary ? lineHeight * 2 + 3 : 0;
  }

  /**
   * Height (mm) of a patient story (title, excerpt, image/QR row), which
   * moves to a new page as one block if it doesn't fit.
   */
  function estimatePatientStoryHeight(
    pdf,
    title,
    contentInfo,
    maxWidth,
    lineHeight,
    titleLineHeight,
    sectionSpacing
  ) {
    let height = 0;
    if (title) {
      pdf.setFontSize(12);
      height +=
        splitTextIntoLines(pdf, title, maxWidth).length * titleLineHeight + 1;
    }
    if (contentInfo.content) {
      pdf.setFontSize(10);
      height +=
        splitTextIntoLines(pdf, contentInfo.content, maxWidth).length *
          lineHeight +
        3;
    }
    const mediaLayout = patientStoryMediaLayout(
      contentInfo.imageData ? contentInfo.imageWidth : 0,
      contentInfo.imageData ? contentInfo.imageHeight : 0,
      contentInfo.qrCodeUrl ? contentInfo.qrCodeWidth : 0,
      contentInfo.qrCodeUrl ? contentInfo.qrCodeHeight : 0,
      maxWidth
    );
    if (mediaLayout.height > 0) {
      height += mediaLayout.height + sectionSpacing;
    }
    return height;
  }

  /**
   * Height (mm) a title must keep on its page for the item after it, matching
   * that item's own page-break check:
   * - chart/calculator: item title + image (+ the chart's summary lines)
   * - placed image: item title + image
   * - patient story: the whole story
   * - anything else: item title + two lines
   * Resolves with 0 when nothing follows.
   */
  function measureItemStart(
    pdf,
    item,
    margin,
    maxWidth,
    lineHeight,
    titleLineHeight,
    sectionSpacing
  ) {
    if (!item || item.classList.contains("canvas-page-break-item")) {
      return Promise.resolve(0);
    }

    const title = getItemTitle(item);
    const itemTitleHeight = title ? titleLineHeight + 1 : 0;

    if (item.classList.contains("canvas-patient-story-item")) {
      return Promise.resolve(
        estimatePatientStoryHeight(
          pdf,
          title,
          getItemContent(item),
          maxWidth,
          lineHeight,
          titleLineHeight,
          sectionSpacing
        )
      );
    }

    if (
      item.classList.contains("canvas-chart-item") ||
      item.classList.contains("canvas-calculator-item")
    ) {
      const isChart = item.classList.contains("canvas-chart-item");
      const element = getCaptureElement(item);
      // Widths addImageToPDF draws them at
      const drawnWidth = isChart ? maxWidth * 0.9 : maxWidth;
      const keepWithNext = isChart
        ? chartKeepWithNext(pdf, getItemContent(item), margin, maxWidth, lineHeight)
        : 0;
      return applyCaptureLayout(element).then(function (restoreLayout) {
        // Measure what the capture will contain
        const restoreExcluded = hideCaptureExcluded(element);
        const rect = element.getBoundingClientRect();
        restoreExcluded();
        restoreLayout();
        const imageHeight = rect.width
          ? (rect.height / rect.width) * drawnWidth
          : 0;
        return itemTitleHeight + imageHeight + keepWithNext;
      });
    }

    if (item.classList.contains("canvas-image-item")) {
      const content = getItemContent(item);
      if (content.type === "image" && content.maxWidth && content.maxHeight) {
        const mmPerPixel = 0.264583;
        const widthMM = content.maxWidth * mmPerPixel;
        const heightMM = content.maxHeight * mmPerPixel;
        const imageHeight =
          widthMM > maxWidth ? heightMM * (maxWidth / widthMM) : heightMM;
        return Promise.resolve(itemTitleHeight + imageHeight);
      }
    }

    return Promise.resolve(itemTitleHeight + lineHeight * 2);
  }

  /**
   * Process items sequentially, capturing images as needed
   */
  function processItemsSequentially(
    items,
    pdf,
    margin,
    maxWidth,
    pageHeight,
    startY,
    lineHeight,
    titleLineHeight,
    sectionSpacing,
    footerSpace
  ) {
    let y = startY;
    let currentIndex = 0;
    const maxContentY = pageHeight - margin - footerSpace; // Bottom margin + footer space
    const pageBreakConfig = { maxContentY: maxContentY, topMargin: margin };

    function processNextItem() {
      if (currentIndex >= items.length) {
        return Promise.resolve();
      }

      const item = items[currentIndex];
      const itemType = item.className;

      // Check if this is a page break item
      if (itemType.includes("canvas-page-break-item")) {
        // Force a new page
        pdf.addPage();
        y = margin;
        currentIndex++;
        return processNextItem();
      }

      // Title items: kept on the same page as the start of the next item.
      // Empty titles are skipped.
      if (item.classList.contains("canvas-title-item")) {
        const heading = item.querySelector(".title-item-heading");
        const text = heading ? heading.textContent.trim() : "";
        const tagName = heading ? heading.tagName.toLowerCase() : "h2";
        currentIndex++;
        if (!text) {
          return processNextItem();
        }

        return measureItemStart(
          pdf,
          items[currentIndex],
          margin,
          maxWidth,
          lineHeight,
          titleLineHeight,
          sectionSpacing
        ).then(function (nextItemStart) {
          const needed =
            measureHeading(pdf, tagName, text, maxWidth) + nextItemStart;
          if (shouldBreakForBlock(y, needed, margin, maxContentY)) {
            pdf.addPage();
            y = margin;
          }
          y = drawHeading(pdf, tagName, text, margin, y, maxWidth, pageBreakConfig);
          return processNextItem();
        });
      }

      const title = getItemTitle(item);
      const contentInfo = getItemContent(item);

      // Handle content based on type
      if (contentInfo.type === "image") {
        // Need to capture as image
        const itemType = item.className;

        if (
          itemType.includes("canvas-chart-item") ||
          itemType.includes("canvas-calculator-item")
        ) {
          return captureElementAsImage(getCaptureElement(item))
            .then(function (imageData) {
              // Calculate actual image height that will be rendered
              const mmPerPixel = 0.264583;
              // Use display dimensions for calculation (not the high-res captured dimensions)
              const displayWidth = imageData.displayWidth || imageData.width;
              const displayHeight = imageData.displayHeight || imageData.height;
              const imgWidthMM = displayWidth * mmPerPixel;
              const imgHeightMM = displayHeight * mmPerPixel;

              // Different sizing for charts vs calculators
              const isChart = itemType.includes("canvas-chart-item");
              const isCalculator = itemType.includes("canvas-calculator-item");
              const targetWidth = isChart ? maxWidth * 0.9 : maxWidth; // Charts 90%, calculators full width (match addImageToPDF)
              const scale = targetWidth / imgWidthMM;
              const actualImageHeight = imgHeightMM * scale;

              // The AI summary and recommendation are flowing text — they can
              // split across pages, so only the title and image have to be
              // kept together. Reserving their full height here used to push
              // charts onto a fresh page and leave the previous one empty.
              // Keep just a couple of lines with the chart so its commentary
              // doesn't start completely detached from it.
              const compactRecommendation =
                isChart && isCompactRecommendation(contentInfo);
              const recFontSize = compactRecommendation
                ? COMPACT_REC_FONT_SIZE
                : 9;
              const recLineHeight = compactRecommendation
                ? COMPACT_REC_LINE_HEIGHT
                : lineHeight;
              const keepWithNext = isChart
                ? chartKeepWithNext(pdf, contentInfo, margin, maxWidth, lineHeight)
                : 0;

              // Space the atomic part needs: title + image (+ keep-with-next)
              const titleHeight = title ? titleLineHeight + 1 : 0;
              const spaceNeeded = titleHeight + actualImageHeight + keepWithNext;

              if (shouldBreakForBlock(y, spaceNeeded, margin, maxContentY)) {
                pdf.addPage();
                y = margin;
              }

              // Add item title (now we know it will be on the same page as the image)
              if (title) {
                pdf.setFontSize(12);
                pdf.setTextColor(30, 51, 86);
                pdf.setFont(undefined, "bold");
                y = addWrappedText(
                  pdf,
                  title,
                  margin,
                  y,
                  maxWidth,
                  titleLineHeight
                );
                pdf.setFont(undefined, "normal");
                // pdf.setTextColor(102, 102, 102);
                y += 1;
              }

              // Add chart/calculator image (pass type info for sizing)
              y = addImageToPDF(
                pdf,
                imageData.data,
                imageData.width,
                imageData.height,
                margin,
                y,
                maxWidth,
                pageHeight,
                margin,
                null,
                null,
                isChart ? "chart" : "calculator",
                footerSpace
              );

              // Calculators are drawn at (margin, y - height), maxWidth wide:
              // keep their reference links clickable
              if (isCalculator) {
                const drawnHeight =
                  (imageData.displayHeight * maxWidth) / imageData.displayWidth;
                addCapturedLinks(
                  pdf,
                  imageData.links,
                  margin,
                  y - drawnHeight,
                  maxWidth,
                  imageData.displayWidth
                );
              }

              // Add recommendation below the chart if present, rendered as
              // real text so hyperlinks stay clickable (only for charts)
              if (
                isChart &&
                contentInfo.recommendationSegments &&
                contentInfo.recommendationSegments.length
              ) {
                y += 3; // Spacing between chart and recommendation

                if (y > maxContentY) {
                  pdf.addPage();
                  y = margin;
                }

                pdf.setFontSize(recFontSize);
                if (contentInfo.recommendationRole) {
                  pdf.setFont(undefined, "bold");
                  pdf.setTextColor(30, 51, 86);
                  y = addWrappedText(
                    pdf,
                    contentInfo.recommendationRole,
                    margin,
                    y,
                    maxWidth,
                    lineHeight,
                    pageBreakConfig
                  );
                  pdf.setFont(undefined, "normal");
                }
                y = addSegmentsWithLinks(
                  pdf,
                  contentInfo.recommendationSegments,
                  margin,
                  y,
                  maxWidth,
                  recLineHeight,
                  pageBreakConfig
                );
                pdf.setTextColor(0, 0, 0);
                y += 2;
              }

              // Add AI summary below the chart if present (only for charts, not calculators)
              if (
                isChart &&
                contentInfo.aiSummary &&
                contentInfo.aiSummary.trim()
              ) {
                y += 3; // Spacing between chart and summary

                // Check if summary fits on current page
                if (y > maxContentY) {
                  pdf.addPage();
                  y = margin;
                }

                // Style summary text (slightly smaller, italic for distinction)
                pdf.setFontSize(9);
                pdf.setFont(undefined, "italic");
                pdf.setTextColor(102, 102, 102); // Gray color
                y = addWrappedText(
                  pdf,
                  contentInfo.aiSummary,
                  margin,
                  y,
                  maxWidth,
                  lineHeight,
                  pageBreakConfig
                );
                pdf.setFont(undefined, "normal");
                y += 2;
              }

              y += sectionSpacing; // Charts have spacing below

              // Calculators have no spacing below

              currentIndex++;
              return processNextItem();
            })
            .catch(function (error) {
              console.error("Error capturing image:", error);
              // Fallback to text
              pdf.setFontSize(10);
              pdf.setTextColor(102, 102, 102);
              const fallbackText = itemType.includes("canvas-chart-item")
                ? "[Chart - Unable to capture image]"
                : "[Calculator - Unable to capture image]";
              y = addWrappedText(
                pdf,
                fallbackText,
                margin,
                y,
                maxWidth,
                lineHeight,
                pageBreakConfig
              );
              y += sectionSpacing;
              currentIndex++;
              return processNextItem();
            });
        } else if (
          itemType.includes("canvas-image-item") &&
          contentInfo.imageData
        ) {
          // Load image from URL
          return new Promise(function (resolve, reject) {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = function () {
              try {
                // Estimate image height. Mirror the addImageToPDF logic:
                // render at displayed size in mm, clamped to page width.
                const mmPerPixel = 0.264583;
                const displayWidth = contentInfo.maxWidth || img.width;
                const displayHeight = contentInfo.maxHeight || img.height;
                const imgWidthMM = displayWidth * mmPerPixel;
                const imgHeightMM = displayHeight * mmPerPixel;

                let estimatedImageHeight = imgHeightMM;
                if (imgWidthMM > maxWidth) {
                  const scale = maxWidth / imgWidthMM;
                  estimatedImageHeight = imgHeightMM * scale;
                }

                // Space needed: title + image. Trailing section spacing is
                // excluded — it doesn't have to fit on the page.
                const titleHeight = title ? titleLineHeight + 1 : 0;
                const spaceNeeded = titleHeight + estimatedImageHeight;

                if (shouldBreakForBlock(y, spaceNeeded, margin, maxContentY)) {
                  pdf.addPage();
                  y = margin;
                }

                // Add item title (now we know it will be on the same page as the image)
                if (title) {
                  pdf.setFontSize(12);
                  pdf.setTextColor(30, 51, 86);
                  pdf.setFont(undefined, "bold");
                  y = addWrappedText(
                    pdf,
                    title,
                    margin,
                    y,
                    maxWidth,
                    titleLineHeight
                  );
                  pdf.setFont(undefined, "normal");
                  y += 1;
                }

                y = addImageToPDF(
                  pdf,
                  contentInfo.imageData,
                  contentInfo.imageWidth,
                  contentInfo.imageHeight,
                  margin,
                  y,
                  maxWidth,
                  pageHeight,
                  margin,
                  contentInfo.maxWidth,
                  contentInfo.maxHeight,
                  null,
                  footerSpace
                );
                y += sectionSpacing + 5; // Extra margin below images
                currentIndex++;
                resolve(processNextItem());
              } catch (error) {
                reject(error);
              }
            };
            img.onerror = function () {
              pdf.setFontSize(10);
              pdf.setTextColor(102, 102, 102);
              y = addWrappedText(
                pdf,
                "[Image - Unable to load]",
                margin,
                y,
                maxWidth,
                lineHeight,
                pageBreakConfig
              );
              y += sectionSpacing;
              currentIndex++;
              resolve(processNextItem());
            };
            img.src = contentInfo.imageData;
          });
        } else {
          // No image data available
          pdf.setFontSize(10);
          pdf.setTextColor(102, 102, 102);
          y = addWrappedText(
            pdf,
            contentInfo.content || "[Content]",
            margin,
            y,
            maxWidth,
            lineHeight,
            pageBreakConfig
          );
          y += sectionSpacing;
          currentIndex++;
          return processNextItem();
        }
      } else {
        // Check if we need a new page (for non-image content)
        if (y > maxContentY) {
          pdf.addPage();
          y = margin;
        }

        // For patient stories, estimate the combined height of title + excerpt
        // + the image/QR media row and page-break upfront if the whole block
        // won't fit. This avoids orphan titles and footer overlap.
        if (itemType.includes("canvas-patient-story-item")) {
          const estimatedHeight = estimatePatientStoryHeight(
            pdf,
            title,
            contentInfo,
            maxWidth,
            lineHeight,
            titleLineHeight,
            sectionSpacing
          );
          // Only page-break if we're not already at the top and the block
          // genuinely won't fit on the current page.
          if (shouldBreakForBlock(y, estimatedHeight, margin, maxContentY)) {
            pdf.addPage();
            y = margin;
          }
        }

        // Add item title
        if (title) {
          pdf.setFontSize(12);

          // Keep-with-next: don't leave a title alone at the foot of a page.
          // (A patient story that already broke above is at the page top, so
          // shouldBreakForBlock returns false and this won't double-break.)
          const titleBlock =
            splitTextIntoLines(pdf, title, maxWidth).length * titleLineHeight +
            1 +
            lineHeight * 2;
          if (shouldBreakForBlock(y, titleBlock, margin, maxContentY)) {
            pdf.addPage();
            y = margin;
          }

          pdf.setTextColor(30, 51, 86);
          pdf.setFont(undefined, "bold");
          const storyUrl = itemType.includes("canvas-patient-story-item")
            ? item.getAttribute("data-story-url")
            : null;
          const titleStartY = y;
          y = addWrappedText(
            pdf,
            title,
            margin,
            y,
            maxWidth,
            titleLineHeight,
            pageBreakConfig
          );
          // Patient story titles link to the full story. Skipped if the title
          // page-broke mid-way (y reset), since the rect would span pages.
          if (storyUrl && y > titleStartY) {
            const linkWidth = Math.min(pdf.getTextWidth(title), maxWidth);
            pdf.link(
              margin,
              titleStartY - titleLineHeight + 1,
              linkWidth,
              y - titleStartY,
              { url: storyUrl }
            );
          }
          pdf.setFont(undefined, "normal");
          y += 1;
        }

        // Text content - check if it contains HTML
        if (contentInfo.content) {
          const itemType = item.className;

          // For text items, check if content has HTML tags
          if (itemType.includes("canvas-text-item")) {
            const contentArea = item.querySelector(".text-item-content");
            let htmlContent = "";

            if (contentArea) {
              // Try to get HTML from WYSIWYG editor
              const wysiwyg = contentArea.querySelector(".wysiwyg-textarea");
              if (wysiwyg && wysiwyg.value) {
                htmlContent = wysiwyg.value;
              } else {
                // Try to get from iframe (TinyMCE)
                const iframe = contentArea.querySelector("iframe");
                if (iframe && iframe.contentDocument) {
                  htmlContent = iframe.contentDocument.body.innerHTML;
                } else {
                  htmlContent = contentArea.innerHTML;
                }
              }
            }

            // Check if content has HTML tags
            if (htmlContent && /<[a-z][\s\S]*>/i.test(htmlContent)) {
              // Render HTML with formatting, once any embedded images are
              // loaded so they can be drawn in place
              return preloadHtmlImages(htmlContent).then(function (images) {
                y = renderHtmlToPDF(
                  pdf,
                  htmlContent,
                  margin,
                  y,
                  maxWidth,
                  pageHeight,
                  margin,
                  footerSpace,
                  images
                );
                y += sectionSpacing;

                // Add spacing between items
                if (currentIndex < items.length - 1) {
                  y += 3;
                }

                currentIndex++;
                return processNextItem();
              });
            } else {
              // Plain text
              pdf.setFontSize(10);
              pdf.setTextColor(102, 102, 102);
              y = addWrappedText(
                pdf,
                contentInfo.content,
                margin,
                y,
                maxWidth,
                lineHeight,
                pageBreakConfig
              );
              y += sectionSpacing;
            }
          } else if (itemType.includes("canvas-patient-story-item")) {
            // Patient story (MCH-211): excerpt full width, then the featured
            // image and a small clickable QR code side by side on one row.
            const storyUrl = item.getAttribute("data-story-url") || "";

            // Helper function to add text content
            function addTextContent() {
              if (contentInfo.content) {
                // Check if we need a new page
                if (y > maxContentY) {
                  pdf.addPage();
                  y = margin;
                }

                pdf.setFontSize(10);
                pdf.setTextColor(102, 102, 102);
                y = addWrappedText(
                  pdf,
                  contentInfo.content,
                  margin,
                  y,
                  maxWidth,
                  lineHeight,
                  pageBreakConfig
                );
                y += 3;
              }
              return Promise.resolve();
            }

            // Load an image and resolve with it, or null if it won't load
            function loadImage(src) {
              return new Promise(function (resolve) {
                if (!src) {
                  resolve(null);
                  return;
                }
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.onload = function () {
                  resolve(img);
                };
                img.onerror = function () {
                  console.error("Error loading patient story image:", src);
                  resolve(null);
                };
                img.src = src;
              });
            }

            // Featured image and QR code side by side, sized by the shared
            // layout helper so this matches the page-break estimate above.
            function addMediaRow() {
              return Promise.all([
                loadImage(contentInfo.imageData),
                loadImage(contentInfo.qrCodeUrl),
              ]).then(function (loaded) {
                const featured = loaded[0];
                const qrImage = loaded[1];

                const layout = patientStoryMediaLayout(
                  featured ? featured.width : 0,
                  featured ? featured.height : 0,
                  qrImage ? qrImage.width : 0,
                  qrImage ? qrImage.height : 0,
                  maxWidth
                );

                if (layout.height <= 0) {
                  return;
                }

                // Keep the row intact rather than splitting image from QR
                if (shouldBreakForBlock(y, layout.height, margin, maxContentY)) {
                  pdf.addPage();
                  y = margin;
                }

                let cursorX = margin + layout.offsetX;

                try {
                  if (layout.image) {
                    pdf.addImage(
                      contentInfo.imageData,
                      "PNG",
                      cursorX,
                      y + (layout.height - layout.image.height) / 2,
                      layout.image.width,
                      layout.image.height
                    );
                    cursorX += layout.image.width + PS_MEDIA_GAP;
                  }

                  if (layout.qr) {
                    // Sits alongside the image, vertically centred against it
                    const qrY = y + (layout.height - layout.qr.height) / 2;
                    pdf.addImage(
                      contentInfo.qrCodeUrl,
                      "PNG",
                      cursorX,
                      qrY,
                      layout.qr.width,
                      layout.qr.height
                    );
                    // Clickable as well as scannable
                    if (storyUrl) {
                      pdf.link(cursorX, qrY, layout.qr.width, layout.qr.height, {
                        url: storyUrl,
                      });
                    }
                  }
                } catch (error) {
                  console.error("Error adding patient story media:", error);
                }

                y += layout.height + sectionSpacing;
              });
            }

            // Excerpt first, then the image/QR row beneath it
            return addTextContent()
              .then(addMediaRow)
              .then(function () {
                currentIndex++;
                return processNextItem();
              });
          } else {
            // For other content types (e.g. fun facts), check if HTML.
            // Embedded images are loaded first so they render in the PDF.
            if (/<[a-z][\s\S]*>/i.test(contentInfo.content)) {
              return preloadHtmlImages(contentInfo.content).then(function (
                images
              ) {
                y = renderHtmlToPDF(
                  pdf,
                  contentInfo.content,
                  margin,
                  y,
                  maxWidth,
                  pageHeight,
                  margin,
                  footerSpace,
                  images
                );
                y += sectionSpacing;

                // Add spacing between items
                if (currentIndex < items.length - 1) {
                  y += 3;
                }

                currentIndex++;
                return processNextItem();
              });
            } else {
              pdf.setFontSize(10);
              pdf.setTextColor(102, 102, 102);
              y = addWrappedText(
                pdf,
                contentInfo.content,
                margin,
                y,
                maxWidth,
                lineHeight,
                pageBreakConfig
              );
              y += sectionSpacing;
            }

            // Add spacing between items
            if (currentIndex < items.length - 1) {
              y += 3;
            }

            currentIndex++;
            return processNextItem();
          }
        } else {
          y += sectionSpacing;
        }

        // Add spacing between items
        if (currentIndex < items.length - 1) {
          y += 3;
        }

        currentIndex++;
        return processNextItem();
      }
    }

    return processNextItem();
  }

  /**
   * Perform the PDF save (upload to hospital)
   */
  function performSave(canvasCharts, JsPDF) {
    // Show loading indicator
    const originalButton = document.querySelector(
      '.toolbar-btn[data-action="save-pdf"]'
    );
    const originalLabel = originalButton
      ? originalButton.querySelector(".toolbar-btn-label").textContent
      : "Save PDF";

    if (originalButton) {
      originalButton.disabled = true;
      const labelElement = originalButton.querySelector(".toolbar-btn-label");
      if (labelElement) {
        labelElement.textContent = "Saving PDF...";
      }
    }

    try {
      // Create PDF
      const pdf = new JsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 25;
      const maxWidth = pageWidth - margin * 2;
      let y = margin;
      const lineHeight = 6;
      const titleLineHeight = 6;
      const sectionSpacing = 8;
      const footerSpace = 10;

      // Extract report header
      const header = extractReportHeader(canvasCharts);

      // Load the background image first so it sits behind all content,
      // then render the header + content on top.
      loadBackgroundImage()
        .then(function (backgroundData) {
          setupBackgroundForPDF(pdf, backgroundData, pageWidth, pageHeight);

          // Render header with logo (async)
          return renderReportHeader(
            pdf,
            header,
            margin,
            pageWidth,
            maxWidth,
            pageHeight,
            lineHeight,
            titleLineHeight,
            sectionSpacing
          );
        })
        .then(function (startY) {
          // Extract all items including page breaks
          const items = canvasCharts.querySelectorAll(PDF_ITEM_SELECTOR);

          // Process items sequentially to handle async image capture
          return processItemsSequentially(
            items,
            pdf,
            margin,
            maxWidth,
            pageHeight,
            startY,
            lineHeight,
            titleLineHeight,
            sectionSpacing,
            footerSpace
          );
        })
        .then(loadFooterLogos)
        .then(function (footerLogos) {
          // Background is drawn per page via setupBackgroundForPDF.
          // Just add footers here.
          const totalPages = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            addPageFooter(
              pdf,
              i,
              totalPages,
              header.reportName,
              header.reportDate,
              pageWidth,
              pageHeight,
              margin,
              footerLogos
            );
          }

          // Generate filename with current date
          const now = new Date();
          const dateStr = now.toISOString().split("T")[0];
          const filename = `report-${dateStr}.pdf`;

          // Get PDF as blob
          const pdfBlob = pdf.output("blob");

          // Upload to server
          return uploadPDFToHospital(pdfBlob, filename, header.reportName);
        })
        .then(function (response) {
          // Restore button state
          if (originalButton) {
            originalButton.disabled = false;
            const labelElement =
              originalButton.querySelector(".toolbar-btn-label");
            if (labelElement) {
              labelElement.textContent = originalLabel;
            }
          }

          // Show success message
          alert("PDF saved successfully to hospital records!");
        })
        .catch(function (error) {
          console.error("Error saving PDF:", error);
          console.error("Error stack:", error.stack);
          alert("Error saving PDF: " + (error.message || "Unknown error") + ". Please check the console for details.");

          // Restore button state
          if (originalButton) {
            originalButton.disabled = false;
            const labelElement =
              originalButton.querySelector(".toolbar-btn-label");
            if (labelElement) {
              labelElement.textContent = originalLabel;
            }
          }
        });
    } catch (error) {
      console.error("Error saving PDF:", error);
      console.error("Error stack:", error.stack);
      alert("Error saving PDF: " + (error.message || "Unknown error") + ". Please check the console for details.");

      // Restore button state
      if (originalButton) {
        originalButton.disabled = false;
        const labelElement = originalButton.querySelector(".toolbar-btn-label");
        if (labelElement) {
          labelElement.textContent = originalLabel;
        }
      }
    }
  }

  /**
   * Upload PDF blob to hospital via AJAX
   */
  function uploadPDFToHospital(pdfBlob, filename, reportName) {
    return new Promise(function (resolve, reject) {
      console.log("Starting PDF upload...");
      console.log("PDF blob size:", pdfBlob.size, "bytes");
      console.log("Filename:", filename);
      console.log("Report name:", reportName);
      
      // Create FormData
      const formData = new FormData();
      formData.append("action", "save_report_pdf");
      formData.append("pdf_file", pdfBlob, filename);
      formData.append("report_name", reportName || "Untitled Report");
      formData.append(
        "nonce",
        window.reportBuilderData && window.reportBuilderData.nonce
          ? window.reportBuilderData.nonce
          : ""
      );

      console.log("FormData prepared, sending AJAX request...");

      // Send AJAX request
      fetch(window.ajaxurl || "/wp-admin/admin-ajax.php", {
        method: "POST",
        body: formData,
        credentials: "same-origin",
      })
        .then(function (response) {
          console.log("Response status:", response.status);
          console.log("Response ok:", response.ok);
          return response.json();
        })
        .then(function (data) {
          console.log("Response data:", data);
          if (data.success) {
            console.log("PDF uploaded successfully!");
            resolve(data.data);
          } else {
            console.error("Upload failed:", data.data);
            reject(new Error(data.data.message || "Failed to save PDF"));
          }
        })
        .catch(function (error) {
          console.error("AJAX error:", error);
          reject(error);
        });
    });
  }

  // Expose function globally immediately
  window.ReportBuilderPDFExport = {
    exportToPDF: exportToPDF,
    saveToPDF: saveToPDF,
  };
})();
